import {formatDate, registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de-CH';
import localeEn from '@angular/common/locales/en';
import localeDeExtra from '@angular/common/locales/extra/de-CH';
import localeEnExtra from '@angular/common/locales/extra/en';
import {Inject, Injectable} from '@angular/core';
import { Observable, of, OperatorFunction } from 'rxjs';
import { catchError, map, share, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpHelper } from '../helpers/HttpHelper';
import { isOnlineEvent } from '../helpers/misc';
import {Event} from '../models/Event';
import { LabelsModel } from '../models/LabelsModel';
import { HTTP_HELPER } from '../providers/http.helper.provider';

@Injectable()
export class LabelsService {

    constructor(
        @Inject(HTTP_HELPER) private http: HttpHelper
    ) {
        const apiCall$ = this.http.get<LabelsModel>(this.buildApiCallUrl());

        this.labelsModel$ = apiCall$
            .pipe(this.saveLabelsModel())
            .pipe(this.handleLabelLoadingError())
            .pipe(share());
    }

    private labelsModel$: Observable<LabelsModel>;
    private loadedLabelsModel: LabelsModel;
    private _language: string;

    get language(): string {
        return this._language;
    }

    private buildApiCallUrl(): string {
        let localizationFileName: string;
        let localizationEndpoint = environment.localizationEndpoint;
        const approot = document.getElementsByTagName('app-root')[0];
        if (approot && approot.getAttribute('language') === 'de') {
            this._language = 'de-ch';
            localizationFileName = '1031.json';
            registerLocaleData(localeDe, 'de-ch', localeDeExtra);
        } else {
            this._language = 'en-us';
            localizationFileName = '1033.json';
            registerLocaleData(localeEn, 'en-us', localeEnExtra);
        }
        if (approot && approot.getAttribute('assetsEndpoint')) {
            localizationEndpoint = approot.getAttribute('assetsEndpoint') + 'Localization/';
        }
        return `${localizationEndpoint}${localizationFileName}`;
    }

    public getLabelsModel(): Observable<LabelsModel> {
        if (this.labelsLoaded()) {
            return of(this.loadedLabelsModel);
        } else {
            return this.labelsModel$;
        }
    }

    public translateLabel(key: string, defaultValue?: string): Observable<string> {
        return this.getLabelsModel().pipe(map((labelsModel) => {
            if (labelsModel.labels[key] !== undefined) {
                return labelsModel.labels[key];
            } else {
                return defaultValue !== undefined ? defaultValue : key;
            }
        }));
    }

    public translateLabelWithParams(key: string, defaultValue?: string, params?: string[]): Observable<string> {
        return this.getLabelsModel().pipe(map((labelsModel) => {
            if (labelsModel.labels[key] !== undefined) {
                const label: string = labelsModel.labels[key];
                return this.format(label, params);
            } else {
                return defaultValue !== undefined ? this.format(defaultValue, params) : key;
            }
        }));
    }

    public format(format: string, args: string[]) {
        let returnValue = format;
        for (let i = 0; i < args.length; i++) {
            const actualValue = typeof (args[i]) === 'undefined' || args[i] == null ? '' : args[i].toString();
            returnValue = returnValue.replace(new RegExp('\\{' + i + '\\}', 'g'), actualValue);
        }
        return returnValue;
    }

    public labelsLoaded(): boolean {
        return this.loadedLabelsModel !== undefined;
    }

    private saveLabelsModel(): OperatorFunction<LabelsModel, LabelsModel> {
        return tap((labelsModel: LabelsModel) => {
            this.loadedLabelsModel = labelsModel;
        });
    }

  /***
   * This function takes an event and returns the event dates as locale dateString
   * If it's a one day event, it will only return that date (i.e. en: Dec 16, 2021 / de: 16. Dez. 2021)
   * If the event lasts several days, it will return the date span (i.e. en:4. - Aug 6, 2021 / de: 4. - 6. Aug. 2021)
   * Depending on the second param, a the time will be concatenated in the string, i.e. (en: Dec 16, 2021 8:30 AM – 5:00 PM / de: 16. Dez. 2021 08:30 – 17:00)
   * @param event which contains a startDate and an endDate
   * @param includeTime per default the Time will be added for one day events and can also be added in any case IncludeTime.ALWAYS or never IncludeTime.NEVER
   */
  public getDateString(event: Event, includeTime: IncludeTime = IncludeTime.SameDaysOnly): string {
    const startDate = new Date(event.startDate.toString());
    const endDate = new Date(event.endDate.toString());
    const dateOptions = {year: 'numeric', month: 'short', day: 'numeric'};

    const isSameYear = startDate.getFullYear() === endDate.getFullYear();
    const isSameMonth = isSameYear && startDate.getMonth() === endDate.getMonth();
    const daySpan = isSameMonth ? endDate.getDate() - startDate.getDate() : 28;
    const timezone = isOnlineEvent(event) ? `\n(${formatDate(startDate, 'z', 'en')})` : '';
    let timeString = includeTime !== IncludeTime.Never ? ` ${this.getTimeString(event)}` : '';
    if (daySpan === 0) {
      return `${startDate.toLocaleDateString(this.language, dateOptions)}${timeString}${timezone}`;
    }
    const daySeparator = daySpan === 1 ? '/' : '–';
    const startDateString = isSameYear ? isSameMonth ? `${startDate.getDate()}.` : `${startDate.toLocaleDateString(this.language, {month: 'short', day: 'numeric'})}` : `${startDate.toLocaleDateString(this.language, dateOptions)}`;
    timeString = includeTime === IncludeTime.Always ? ` ${this.getTimeString(event)}` : '';
    return `${startDateString} ${daySeparator} ${endDate.toLocaleDateString(this.language, dateOptions)}${timeString}${timezone}`;
  }

  public getTimeString(event: Event): string {
    const startDate = new Date(event.startDate.toString());
    const endDate = new Date(event.endDate.toString());
    const timeOptions = {hour: 'numeric', minute: 'numeric'};
    return `${startDate.toLocaleTimeString(this.language, timeOptions)} – ${endDate.toLocaleTimeString(this.language, timeOptions)}`;
  }

    private handleLabelLoadingError(): OperatorFunction<LabelsModel, LabelsModel> {
        return catchError((error, caught) => {
            this.loadedLabelsModel = {
                labels: {},
                isJapanese: false
            };
            return of(this.loadedLabelsModel);
        });
    }
}

export enum IncludeTime {
  Always = 'always',
  SameDaysOnly = 'sameDaysOnly',
  Never = 'never',
}
